/**
 * Login front-door redirect (governance-suite)
 *
 * dac-001-4d-2: retires the in-suite oracle-bridge v1 login. The suite no longer
 * hosts an email/password form or a legacy Internet-Identity login button — the
 * hosted identity-service front-door (registration-suite) authenticates the member
 * (email + password, TOTP) and redirects back to `<this-origin>?code=<one-time>`,
 * which the pre-mount `runIdentityCodeBootstrap` (App.tsx) exchanges for a JWT.
 * Internet Identity is now a POST-login link step (/onboarding), NOT a login method
 * — mirroring dao-admin-suite and dao-suite, which host no in-suite login form.
 *
 * governance keeps `redirectBehavior="local"` on its ProtectedRoute, so an
 * unauthenticated member is bounced to `/login?returnUrl=<original>` (in-SPA) and
 * this component performs the actual cross-origin redirect to the front-door on
 * mount. It preserves the deep-link by stashing the validated `returnUrl` into the
 * sessionStorage key `runIdentityCodeBootstrap` restores after the `?code=` return.
 *
 * @module Login
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { validateReturnUrl } from '@hello-world-dao/auth';
import {
  buildLoginFrontdoorUrl,
  stashExplicitDeepLinkReturnPath,
} from '@/services/identity-service-client';

// FOS-5.6.5: the allow-list validateReturnUrl checks the `returnUrl` param against,
// so a crafted `?returnUrl=` can never redirect the member off-suite (open-redirect
// guard). Preserved from the retired in-suite form.
const GOVERNANCE_ALLOWED_PATHS = [
  '/discussions',
  '/proposals',
  '/voting',
  '/my-votes',
  '/settings',
  '/profile',
  '/admin',
  '/dashboard',
];

export function LoginFrontdoorRedirect() {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // The intended in-suite destination lives in the `returnUrl` param that
    // ProtectedRoute (redirectBehavior="local") appended when it bounced the
    // unauthenticated member here. Validate it against the allow-list so a crafted
    // value can't become an open redirect; defaults to /discussions.
    const returnUrl = validateReturnUrl(searchParams.get('returnUrl'), {
      allowedPaths: GOVERNANCE_ALLOWED_PATHS,
      defaultRedirect: '/discussions',
    });

    // Call order matters: buildLoginFrontdoorUrl() internally stashes the CURRENT
    // path (the bare `/login?returnUrl=…`), which is NOT the member's destination.
    // Build the URL first, THEN overwrite the stash with the validated returnUrl so
    // runIdentityCodeBootstrap restores the right deep-link after the ?code= return.
    const frontdoorUrl = buildLoginFrontdoorUrl();
    stashExplicitDeepLinkReturnPath(returnUrl);
    window.location.assign(frontdoorUrl);
  }, [searchParams]);

  // Neutral loader while the full-document redirect to the hosted front-door runs.
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500" />
    </div>
  );
}

export default LoginFrontdoorRedirect;
